// DEPENDENCIES
import React, { useState, useMemo, useRef, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./RFA.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";
import { unsavedChangesGuardRef } from "../../../context/unsavedChangesGuard";

// MUI COMPONENTS
import {
	Alert,
	AlertTitle,
	Autocomplete,
	Button,
	ButtonGroup,
	CircularProgress,
	Collapse,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	Chip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";

// CUSTOM COMPONENTS
import J2ErrorDialog from "../../Misc/J2ErrorDialog/J2ErrorDialog";
import J2MultilineTextField from "../../Misc/J2MultilineTextField/J2MultilineTextField";
import { getCurrentTimeUTC } from "../../../constants/dateTime";

// Drafts
import RFADraftsDrawer from "./RFADraftsDrawer";

// CONSTANTS
const ELEMENT_SIZE = "medium";

const searchByOptionsMap = new Map([
	["email", "Email"],
	["lastname", "Last Name"],
]);

// REACT COMPONENT
const RFA = () => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS (for autocompletes)
	const dealNameRef = useRef(null);
	const clientSearchByValueRef = useRef(null);

	// API RESULT STATES
	const [dealOptions, setDealOptions] = useState([]);
	const [clientOptions, setClientOptions] = useState([]);
	const [propertyOptions, setPropertyOptions] = useState([]);
	const [projectOptions, setProjectOptions] = useState([]);
	const [phaseOptions, setPhaseOptions] = useState([]);

	// CREATE NEW/SEARCH EXISTING STATES
	const [createNewClientEnabled, setCreateNewClientEnabled] = useState(false);
	const [searchExistingClientEnabled, setSearchExistingClientEnabled] =
		useState(true);

	// DETAILED VIEW/SEARCH BAR COLLAPSE STATES
	const [dealDetailedViewOpen, setDealDetailedViewOpen] = useState(false);
	const [clientDetailedViewOpen, setClientDetailedViewOpen] = useState(false);

	const [clientSearchBarOpen, setClientSearchBarOpen] = useState(true);

	// SEARCH BAR STATES
	const [clientSearchByParameter, setClientSearchByParameter] =
		useState("lastname");
	const [clientSearchByValue, setClientSearchByValue] = useState("");

	// DETAILED VIEW STATES
	const [dealInformation, setDealInformation] = useState({
		id: "",
		name: "",
		amount: "",
		proposal_signed_date: "",
		current_amendment_number: 0,
		oic: "",
	});

	const [clientInformation, setClientInformation] = useState({
		email: "",
		firstName: "",
		lastName: "",
		recordID: "",
	});

	// SCOPE OF SERVICES STATES
	const [numberOfServices, setNumberOfServices] = useState(1);
	const [contractType, setContractType] = useState("");

	const [serviceScopes, setServiceScopes] = useState({
		serviceScope1: "",
		serviceScope2: "",
		serviceScope3: "",
		serviceScope4: "",
		serviceScope5: "",
		serviceScope6: "",
		serviceScope7: "",
		serviceScope8: "",
	});

	const [serviceScopeCosts, setServiceScopeCosts] = useState({
		serviceScope1Cost: "",
		serviceScope2Cost: "",
		serviceScope3Cost: "",
		serviceScope4Cost: "",
		serviceScope5Cost: "",
		serviceScope6Cost: "",
		serviceScope7Cost: "",
		serviceScope8Cost: "",
	});

	const loggingPayload = useRef({
		submitClickedTime: "",
		submittedTime: "",
		errorMessage: "",
		userEmailRetrievedTime: "",
		recipientHubspotCreatedFoundTime: "",
		amendmentCreatedTime: "",
	});

	// AMENDMENT INFORMATION STATES
	const [amendedScopeSelection, setAmendedScopeSelection] = useState({
		property: null,
		project: null,
		phase: null,
	});
	const [additionalNotes, setAdditionalNotes] = useState("");

	// MISC. STATES
	const [isSubmitting, setIsSubmitting] = useState(false);

	// SUCCESS/ERROR STATES
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const [isSuccess, setIsSuccess] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [fieldErrors, setFieldErrors] = useState({});
	const [validationIssues, setValidationIssues] = useState([]);

	// DRAFT STATES
	// Phones (same breakpoint as App.js): no page title (the mobile top bar
	// shows it) and no drafts
	const isMobile = useMediaQuery("(max-width: 767px)");
	const [draftsDrawerOpen, setDraftsDrawerOpen] = useState(false);
	const [currentDraftID, setCurrentDraftID] = useState(null);
	const [currentDraftName, setCurrentDraftName] = useState("");
	const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
	const [draftNameInput, setDraftNameInput] = useState("");
	const [isSavingDraft, setIsSavingDraft] = useState(false);
	const [newRFAConfirmOpen, setNewRFAConfirmOpen] = useState(false);
	const [draftLoadVersion, setDraftLoadVersion] = useState(0);
	const lastSavedDraftSnapshotRef = useRef(null);
	const pristineFormSnapshotRef = useRef(null);
	const isDraftDirtyRef = useRef(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFA",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the RFA page",
		});
	}, []);

	// Browsers only allow a generic, non-customizable confirmation here (the
	// text set on returnValue is ignored by all modern browsers) - this is
	// the standard mechanism for "unsaved changes" warnings on tab close,
	// refresh, or navigating to a new URL.
	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (isDraftDirtyRef.current) {
				event.preventDefault();
				event.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () =>
			window.removeEventListener("beforeunload", handleBeforeUnload);
	}, []);

	// Covers in-app navigation (e.g. clicking another tool in the sidebar),
	// which beforeunload can't see since this app's BrowserRouter swaps
	// routes client-side with no real page unload. GuardedRouterLink (used
	// throughout Navigation.js) checks this on every link click.
	useEffect(() => {
		unsavedChangesGuardRef.current = () => isDraftDirtyRef.current;
		return () => {
			unsavedChangesGuardRef.current = null;
		};
	}, []);

	// Runs after mount, after loading a draft, and after "New RFA" resets the
	// form - each of those is a point where the form should read as "clean."
	// When a draft is loaded this baselines lastSavedDraftSnapshotRef; when
	// there's no loaded draft (a blank/reset form) it baselines
	// pristineFormSnapshotRef instead, so the dirty check below also catches
	// someone filling out a brand-new RFA that was never saved as a draft.
	useEffect(() => {
		const snapshot = JSON.stringify({
			draftName: currentDraftName,
			...buildDraftFormFields(),
		});
		if (currentDraftID) {
			lastSavedDraftSnapshotRef.current = snapshot;
		} else {
			pristineFormSnapshotRef.current = snapshot;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftLoadVersion]);

	useEffect(() => {
		fetchAmendedScopeOptions();
	}, []);

	useEffect(() => {
		if (!dealInformation.id || projectOptions.length === 0) {
			return;
		}

		const matchingProject = projectOptions.find(
			(project) => String(project.hubspot_id) === String(dealInformation.id)
		);

		if (!matchingProject) {
			return;
		}

		const parentProperty = propertyOptions.find(
			(property) => property.id === matchingProject.property_id
		);

		setAmendedScopeSelection((prevState) => ({
			property: parentProperty || prevState.property,
			project: matchingProject,
			phase:
				prevState.phase?.project_id === matchingProject.id
					? prevState.phase
					: null,
		}));
	}, [dealInformation.id, projectOptions, propertyOptions]);

	// DEAL INPUT HANDLERS
	const dealNameSelected = (event, newValue) => {
		clearFieldError("dealName");
		if (newValue && newValue.properties) {
			// if a deal name is selected and the user didn't just clear the autocomplete
			setDealDetailedViewOpen(true);

			setDealInformation({
				id: newValue.properties.hs_object_id,
				name: newValue.properties.dealname,
				amount: newValue.properties.amount ?? "N/A",
				proposal_signed_date: !newValue.properties.proposal_signed_date
					? "N/A"
					: dayjs(newValue.properties.proposal_signed_date).format(
							"MM/DD/YYYY"
					  ),
				current_amendment_number: !newValue.properties
					.current_amendment_number
					? 1
					: parseInt(newValue.properties.current_amendment_number) +
					  1,
				oic: newValue.properties.oic ?? "N/A",
			});
		} else {
			// if the user cleared the autocomplete
			setDealDetailedViewOpen(false);

			// clear all the previous values that were in before
			setDealInformation({
				id: "",
				name: "",
				amount: "",
				proposal_signed_date: "",
				current_amendment_number: 0,
				oic: "",
			});

			setDealOptions([]);
		}
	};

	// CLIENT INPUT HANDLERS
	const createNewClientClicked = () => {
		// if the user clicks the button while the button is already selected, do nothing
		if (createNewClientEnabled) {
			return;
		}

		// close client search bar
		setClientSearchBarOpen(false);
		clearFieldError("recipientSelection");

		// enable the create new client view
		setCreateNewClientEnabled(true);
		setSearchExistingClientEnabled(false);

		// clear all the previous values that were in before
		setClientSearchByValue("");

		setClientInformation({
			email: "",
			firstName: "",
			lastName: "",
			recordID: "",
		});

		// clear the searchbyvalue autocomplete
		const clientSearchByValueEle =
			clientSearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator"
			)[0];
		if (clientSearchByValueEle) clientSearchByValueEle.click();

		// open the detailed view
		setClientDetailedViewOpen(true);
	};

	const searchExistingClientClicked = (override = false) => {
		// override is for when all the inputs are to be cleared, not just a repeated click on the search existing client button when already selected
		if (searchExistingClientEnabled && !override) {
			return;
		}

		// open the client search bar
		setClientSearchBarOpen(true);
		setClientDetailedViewOpen(false);

		// enable the search existing client view
		setSearchExistingClientEnabled(true);
		setCreateNewClientEnabled(false);

		// clear all the previous values that were in before, set parameter back to lastname (default)
		setClientSearchByParameter("lastname");
		setClientSearchByValue("");
		clearFieldError("recipientEmail");
		clearFieldError("recipientFirstName");
		clearFieldError("recipientLastName");

		setClientInformation({
			email: "",
			firstName: "",
			lastName: "",
			recordID: "",
		});
	};

	const clientSearchByParameterSelected = (event) => {
		const newParameter = event.target.value;
		setClientSearchByParameter(newParameter);
	};

	const clientSearchByValueSelected = (event, newValue) => {
		clearFieldError("recipientSelection");
		if (newValue && newValue.properties) {
			// if the searchByValue is selected and the user didn't just clear the autocomplete
			setClientDetailedViewOpen(true);

			setClientInformation({
				email: newValue.properties.email,
				firstName: newValue.properties.firstname,
				lastName: newValue.properties.lastname,
				recordID: newValue.properties.hs_object_id,
			});
		} else {
			// if the user cleared the autocomplete
			setClientDetailedViewOpen(false);

			setClientInformation({
				email: "",
				firstName: "",
				lastName: "",
				recordID: "",
			});

			setClientOptions([]);
		}
	};

	const clientInformationChanged = (event) => {
		const fieldNameMap = {
			email: "recipientEmail",
			firstName: "recipientFirstName",
			lastName: "recipientLastName",
		};
		clearFieldError(fieldNameMap[event.target.name]);
		setClientInformation((prevState) => ({
			...prevState,
			[event.target.name]: event.target.value,
		}));
	};

	// SCOPE OF SERVICES INPUT HANDLERS
	const addServicePressed = () => {
		if (numberOfServices === 8) {
			return;
		}
		setNumberOfServices(numberOfServices + 1);
	};

	const removeServicePressed = () => {
		if (numberOfServices === 1) {
			return;
		}
		clearFieldError(`serviceScope${numberOfServices}`);
		clearFieldError(`serviceScope${numberOfServices}Cost`);
		setNumberOfServices(numberOfServices - 1);
	};

	const serviceScopeChanged = (event) => {
		clearFieldError(event.target.name);
		setServiceScopes((prevState) => ({
			...prevState,
			[event.target.name]: event.target.value,
		}));
	};

	const serviceScopeCostChanged = (event) => {
		clearFieldError(event.target.name);
		setServiceScopeCosts((prevState) => ({
			...prevState,
			[event.target.name]: event.target.value,
		}));
	};

	const contractTypeChanged = (event) => {
		clearFieldError("contractType");
		setContractType(event.target.value);
	};

	// AMENDMENT INFORMATION INPUT HANDLERS
	const additionalNotesChanged = (event) => {
		setAdditionalNotes(event.target.value);
	};

	const amendedPropertySelected = (event, newValue) => {
		if (newValue) {
			clearFieldError("amendedProperty");
		}

		setAmendedScopeSelection({
			property: newValue,
			project: null,
			phase: null,
		});
	};

	const amendedProjectSelected = (event, newValue) => {
		if (newValue) {
			clearFieldError("amendedProperty");
			clearFieldError("amendedProject");
		}

		const parentProperty = newValue
			? propertyOptions.find(
					(property) => property.id === newValue.property_id
			  ) || null
			: amendedScopeSelection.property;

		setAmendedScopeSelection((prevState) => ({
			...prevState,
			property: newValue ? parentProperty : prevState.property,
			project: newValue,
			phase: null,
		}));
	};

	const amendedPhaseSelected = (event, newValue) => {
		if (!newValue) {
			setAmendedScopeSelection((prevState) => ({
				...prevState,
				phase: null,
			}));
			return;
		}

		const parentProject =
			projectOptions.find((project) => project.id === newValue.project_id) ||
			null;
		const parentProperty = parentProject
			? propertyOptions.find(
					(property) => property.id === parentProject.property_id
			  ) || null
			: null;

		clearFieldError("amendedProperty");
		clearFieldError("amendedProject");
		clearFieldError("amendedPhase");

		setAmendedScopeSelection((prevState) => ({
			property: parentProperty || prevState.property,
			project: parentProject || prevState.project,
			phase: newValue,
		}));
	};

	// API FUNCTIONS
	const dealNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setDealOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/deals",
						{
							dealName: event.target.value,
						}
					);

					// include only deals in valid active/on-hold/amendment stages
					const filtered = res.data?.filter(
						(deal) =>
							deal?.properties?.dealstage === "10201527" ||
							deal?.properties?.dealstage === "10201530" ||
							deal?.properties?.dealstage === "1341308700" ||
							deal?.properties?.dealstage === "1341308701" ||
							deal?.properties?.dealstage === "1341308702" ||
							deal?.properties?.dealstage === "1341308703" ||
							deal?.properties?.dealstage === "1341308704"
					);

					setDealOptions(filtered ?? []);
				} catch (error) {
					postAPICall(
						instance,
						accounts[0],
						"/api/logs/automation/add",
						{
							employeeID: employeeID,
							date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
							message:
								"Error fetching deal records from HubSpot.",
							success: false,
							tool_name: "RFA",
						}
					);
				}
			}, 500),
		[]
	);

	const clientSearchByValueChanged = useMemo(
		() =>
			debounce(async (event) => {
				const textLength = event.target.value.length;

				if (textLength < 3) {
					setClientOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/contacts",
						{
							searchParameter: clientSearchByParameter,
							value: event.target.value,
						}
					);

					setClientOptions(res.data);
				} catch (error) {
					postAPICall(
						instance,
						accounts[0],
						"/api/logs/automation/add",
						{
							employeeID: employeeID,
							date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
							message:
								"Error fetching contact records from HubSpot.",
							success: false,
							tool_name: "RFA",
						}
					);
				}
			}, 500),
		[clientSearchByParameter]
	); // this [clientSearchByParameter] is the dependency array, which tells the useMemo hook to re-render when clientSearchByParameter is updated

	const createNewClientRecordInHubspot = async () => {
		// makes sure required data is inputted
		if (
			!clientInformation.email ||
			!clientInformation.firstName ||
			!clientInformation.lastName
		) {
			errorMessageOpened(
				"Make sure client first name, client last name, and email are all filled out. If you believe this to be an error, contact your system administrator."
			);
			// no submit rfa log record here because the validation should catch this before reaching this point and submitrfapressed will log it
			return;
		}

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/addContact",
				{
					email: clientInformation.email,
					firstName: clientInformation.firstName,
					lastName: clientInformation.lastName,
				}
			);

			// log the time the recipient hubspot contact was created
			loggingPayload.current.recipientHubspotCreatedFoundTime =
				getCurrentTimeUTC();

			setClientInformation((prevState) => ({
				...prevState,
				recordID: res.data.id,
			}));

			// setClientRecordID(res.data.id);
			return res.data.id;
		} catch (error) {
			errorMessageOpened(
				"Error creating a contact record in HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator."
			);
		}
	};

	// UI HELPER FUNCTIONS
	const errorMessageClosed = () => {
		setIsError(false);
		setErrorMessage("");
	};

	const successMessageClosed = () => {
		setIsSuccess(false);
		setSuccessMessage("");
	};

	const errorMessageOpened = (errorMessage, adminErrorMessage) => {
		setIsError(true);
		setErrorMessage(errorMessage);
		loggingPayload.current.errorMessage =
			errorMessage + (adminErrorMessage ? ` | ${adminErrorMessage}` : "");
		successMessageClosed();
	};

	const successMessageOpened = (successMessage) => {
		setIsSuccess(true);
		setSuccessMessage(successMessage);
		errorMessageClosed();
	};

	const validationSummaryClosed = () => {
		setValidationIssues([]);
	};

	const clearFieldError = (fieldName) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[fieldName]) {
				return prevErrors;
			}

			const updatedErrors = { ...prevErrors };
			delete updatedErrors[fieldName];
			return updatedErrors;
		});

		setValidationIssues((prevIssues) =>
			prevIssues.filter((issue) => issue.fieldName !== fieldName)
		);
	};

	const focusValidationField = (fieldName) => {
		const fieldIdMap = {
			dealName: "rfaDealName",
			amendedProperty: "rfaAmendedProperty",
			amendedProject: "rfaAmendedProject",
			amendedPhase: "rfaAmendedPhase",
			recipientSelection: "clientSearchByFields",
			recipientEmail: "rfaRecipientEmail",
			recipientFirstName: "rfaRecipientFirstName",
			recipientLastName: "rfaRecipientLastName",
			contractType: "rfaContractType",
			serviceScope1: "rfaServiceScope1",
			serviceScope2: "rfaServiceScope2",
			serviceScope3: "rfaServiceScope3",
			serviceScope4: "rfaServiceScope4",
			serviceScope5: "rfaServiceScope5",
			serviceScope6: "rfaServiceScope6",
			serviceScope7: "rfaServiceScope7",
			serviceScope8: "rfaServiceScope8",
			serviceScope1Cost: "rfaServiceScope1Cost",
			serviceScope2Cost: "rfaServiceScope2Cost",
			serviceScope3Cost: "rfaServiceScope3Cost",
			serviceScope4Cost: "rfaServiceScope4Cost",
			serviceScope5Cost: "rfaServiceScope5Cost",
			serviceScope6Cost: "rfaServiceScope6Cost",
			serviceScope7Cost: "rfaServiceScope7Cost",
			serviceScope8Cost: "rfaServiceScope8Cost",
		};

		const target = document.getElementById(fieldIdMap[fieldName]);
		if (!target) {
			return;
		}

		target.scrollIntoView({ behavior: "smooth", block: "center" });
		if (typeof target.focus === "function") {
			window.setTimeout(() => target.focus(), 100);
		}
	};

	const clearLoggingFields = () => {
		loggingPayload.current.submitClickedTime = "";
		loggingPayload.current.submittedTime = "";
		loggingPayload.current.errorMessage = "";
		loggingPayload.current.userEmailRetrievedTime = "";
		loggingPayload.current.recipientHubspotCreatedFoundTime = "";
		loggingPayload.current.amendmentCreatedTime = "";
	};

	const clearInputFields = () => {
		setFieldErrors({});
		setValidationIssues([]);
		setDealOptions([]);
		setClientOptions([]);

		setNumberOfServices(1);
		setContractType("");

		setServiceScopes({
			serviceScope1: "",
			serviceScope2: "",
			serviceScope3: "",
			serviceScope4: "",
			serviceScope5: "",
			serviceScope6: "",
			serviceScope7: "",
			serviceScope8: "",
		});

		setServiceScopeCosts({
			serviceScope1Cost: "",
			serviceScope2Cost: "",
			serviceScope3Cost: "",
			serviceScope4Cost: "",
			serviceScope5Cost: "",
			serviceScope6Cost: "",
			serviceScope7Cost: "",
			serviceScope8Cost: "",
		});

		setAdditionalNotes("");
		setAmendedScopeSelection({
			property: null,
			project: null,
			phase: null,
		});

		searchExistingClientClicked(true);
		const clientSearchByValueEle =
			clientSearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator"
			)[0];
		if (clientSearchByValueEle) clientSearchByValueEle.click();

		setDealDetailedViewOpen(false);

		setDealInformation({
			id: "",
			name: "",
			amount: "",
			proposal_signed_date: "",
			current_amendment_number: 0,
			oic: "",
		});

		const dealNameEle = dealNameRef.current.getElementsByClassName(
			"MuiAutocomplete-clearIndicator"
		)[0];
		if (dealNameEle) dealNameEle.click();
	};

	// MISC. HELPER FUNCTIONS
	const fetchAmendedScopeOptions = async () => {
		try {
			const [propertyRes, projectRes, phaseRes] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/jobs/property/all"),
				getAPICall(instance, accounts[0], "/api/jobs/project/all"),
				getAPICall(instance, accounts[0], "/api/jobs/phases/all"),
			]);

			setPropertyOptions(propertyRes.data || []);
			setProjectOptions(projectRes.data || []);
			setPhaseOptions(phaseRes.data || []);
		} catch (error) {
			console.log("Error fetching amended scope options:", error);
		}
	};

	function debounce(callback, wait) {
		let timeoutId = null;
		return (...args) => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => {
				callback.apply(null, args);
			}, wait);
		};
	}

	const trimString = (value) =>
		typeof value === "string" ? value.trim() : value;

	const isBlank = (value) => {
		if (typeof value === "string") {
			return trimString(value).length === 0;
		}
		return value === null || value === undefined;
	};

	const getOrdinal = (num) => {
		if (num === 1) return "first";
		if (num === 2) return "second";
		if (num === 3) return "third";
		if (num === 4) return "fourth";
		if (num === 5) return "fifth";
		if (num === 6) return "sixth";
		if (num === 7) return "seventh";
		return "eighth";
	};

	const pppName = [
		amendedScopeSelection.property?.name,
		amendedScopeSelection.project?.display_name ||
			amendedScopeSelection.project?.name,
		amendedScopeSelection.phase?.name,
	]
		.filter(Boolean)
		.join(": ");

	const amendmentLineItems = Array.from(
		{ length: numberOfServices },
		(_, index) => {
			const rowNumber = index + 1;
			const scope =
				trimString(serviceScopes[`serviceScope${rowNumber}`]) || "";
			const cost =
				trimString(
					serviceScopeCosts[`serviceScope${rowNumber}Cost`]
				) || "";

			if (!scope && !cost) {
				return null;
			}

			return `Line Item ${rowNumber}: ${scope}${
				cost ? ` ($${Number(cost).toLocaleString("en-US")})` : ""
			}`;
		}
	)
		.filter(Boolean)
		.join(" | ");

	const contractTypeLabelMap = {
		fixed_fee: "Fixed Fee",
		time_and_cost: "Time & Cost",
	};

	const amendmentInformation = [
		amendedScopeSelection.phase?.name
			? `Phase: ${amendedScopeSelection.phase.name.toUpperCase()}`
			: "",
		pppName ? `Job being amended: ${pppName}` : "",
		contractType
			? `Contract Type: ${
					contractTypeLabelMap[contractType] || contractType
			  }`
			: "",
		amendmentLineItems,
		trimString(additionalNotes)
			? `Notes: ${trimString(additionalNotes)}`
			: "",
	]
		.filter(Boolean)
		.join(" | ");

	const filteredProjectOptions = amendedScopeSelection.property
		? projectOptions.filter(
				(project) =>
					project.property_id === amendedScopeSelection.property.id
		  )
		: [];

	const filteredPhaseOptions = phaseOptions.filter((phase) => {
		if (
			amendedScopeSelection.property &&
			phase.property_id !== amendedScopeSelection.property.id
		) {
			return false;
		}

		if (
			amendedScopeSelection.project &&
			phase.project_id !== amendedScopeSelection.project.id
		) {
			return false;
		}

		return true;
	});

	const validateInputFields = () => {
		const nextFieldErrors = {};
		const nextValidationIssues = [];
		const addFieldError = (fieldName, message) => {
			if (nextFieldErrors[fieldName]) {
				return;
			}
			nextFieldErrors[fieldName] = message;
			nextValidationIssues.push({ fieldName, message });
		};

		if (!dealInformation.name) {
			addFieldError(
				"dealName",
				"Deal Information > Deal Name: Select a deal."
			);
		}

		if (!amendedScopeSelection.property) {
			addFieldError(
				"amendedProperty",
				"Property / Project / Phase Information > Property: Select a property."
			);
		}

		if (!amendedScopeSelection.project) {
			addFieldError(
				"amendedProject",
				"Property / Project / Phase Information > Project: Select a project."
			);
		}

		if (!amendedScopeSelection.phase) {
			addFieldError(
				"amendedPhase",
				"Property / Project / Phase Information > Phase: Select a phase."
			);
		}

		if (createNewClientEnabled) {
			if (isBlank(clientInformation.email)) {
				addFieldError(
					"recipientEmail",
					"Recipient Information > Email: Enter an email address."
				);
			}
			if (isBlank(clientInformation.firstName)) {
				addFieldError(
					"recipientFirstName",
					"Recipient Information > First Name: Enter a first name."
				);
			}
			if (isBlank(clientInformation.lastName)) {
				addFieldError(
					"recipientLastName",
					"Recipient Information > Last Name: Enter a last name."
				);
			}
		} else if (!clientInformation.recordID) {
			addFieldError(
				"recipientSelection",
				"Recipient Information: Select an existing recipient or switch to Create New."
			);
		}

		if (!contractType) {
			addFieldError(
				"contractType",
				"Scope of Service(s) > Contract Type: Please select a contract type."
			);
		}

		for (let i = 1; i <= numberOfServices; i++) {
			const scopeFieldName = `serviceScope${i}`;
			const costFieldName = `serviceScope${i}Cost`;
			const ordinal = getOrdinal(i);
			const costValue = trimString(serviceScopeCosts[costFieldName] || "");

			if (isBlank(serviceScopes[scopeFieldName])) {
				addFieldError(
					scopeFieldName,
					`Scope of Services > Service ${i}: Enter the ${ordinal} service.`
				);
			}

			if (isBlank(costValue)) {
				addFieldError(
					costFieldName,
					`Scope of Services > Service ${i} Cost: Enter the ${ordinal} estimated cost.`
				);
			} else if (!/^\d+$/.test(costValue)) {
				addFieldError(
					costFieldName,
					`Scope of Services > Service ${i} Cost: Enter a whole number with no symbols or decimals.`
				);
			} else if (parseInt(costValue, 10) < 0) {
				addFieldError(
					costFieldName,
					`Scope of Services > Service ${i} Cost: Enter a non-negative whole number.`
				);
			}
		}

		setFieldErrors(nextFieldErrors);
		setValidationIssues(nextValidationIssues);

		if (nextValidationIssues.length > 0) {
			focusValidationField(nextValidationIssues[0].fieldName);
			errorMessageOpened(
				`Please fix the highlighted fields (${nextValidationIssues.length}) before submitting.`
			);
			return false;
		}

		return true;
	};

	// RFA SUBMITTED
	const submitRFAPressed = async () => {
		// log the time the submit button was clicked
		loggingPayload.current.submitClickedTime = getCurrentTimeUTC();

		try {
			// this adds a loading spinner and disables the button
			setIsSubmitting(true);

			// retrieve MSAL user email and handle error if necessary
			const userRes = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/byID",
				{ id: employeeID }
			);

			if (
				userRes.data.length === 0 ||
				userRes.data.error ||
				!userRes.data[0].last_name
			) {
				errorMessageOpened(
					"Error retrieving Microsoft user profile. Refresh your browser and try again. If the problem persists, contact your system administrator."
				);
				setIsSubmitting(false);
				// log the error message
				await submitRFALog({
					errorMessage:
						"Error retrieving Microsoft user profile. Refresh your browser and try again. If the problem persists, contact your system administrator.",
				});
				return;
			} else {
				// log the time the user email was retrieved
				loggingPayload.current.userEmailRetrievedTime =
					getCurrentTimeUTC();
			}

			const userName =
				userRes.data[0].first_name + " " + userRes.data[0].last_name;

			// Validate form fields before attempting record creation.
			if (!validateInputFields()) {
				setIsSubmitting(false);
				await submitRFALog({
					errorMessage:
						"Error validating input fields. User did not fill out required fields or filled them out incorrectly.",
				});
				return;
			}

			// this checks to see if an existing client is selected, and if not, creates a new client record in HubSpot
			var associatedClientRecordID = clientInformation.recordID;

			if (!clientInformation.recordID) {
				associatedClientRecordID =
					await createNewClientRecordInHubspot();

				if (!associatedClientRecordID) {
					setIsSubmitting(false);
					// createNewClientRecordInHubspot already sets a user-facing error.
					await submitRFALog({
						errorMessage:
							loggingPayload.current.errorMessage ||
							"Error creating client record in Hubspot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
					});
					return;
				}
			} else {
				// log the time the recipient hubspot contact was found
				loggingPayload.current.recipientHubspotCreatedFoundTime =
					getCurrentTimeUTC();
			}

			const primaryTaskOwnerID =
				dealInformation.oic === "OR"
					? "84145049" // Kerry
					: "57152858"; // Kelli
			const taskOwnerIDs =
				primaryTaskOwnerID === "57152858"
					? [primaryTaskOwnerID, "64990791"]
					: [primaryTaskOwnerID];

			// sends a create amendment request to Express server which relays data to hubspot api
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/createAmendment",
				{
					dealID: dealInformation.id,
					amendmentNumber: dealInformation.current_amendment_number,
					contactID: associatedClientRecordID,
					lineItem1: serviceScopes.serviceScope1 ?? "",
					lineItem2: serviceScopes.serviceScope2 ?? "",
					lineItem3: serviceScopes.serviceScope3 ?? "",
					lineItem4: serviceScopes.serviceScope4 ?? "",
					lineItem5: serviceScopes.serviceScope5 ?? "",
					lineItem6: serviceScopes.serviceScope6 ?? "",
					lineItem7: serviceScopes.serviceScope7 ?? "",
					lineItem8: serviceScopes.serviceScope8 ?? "",
					lineItem1Cost: serviceScopeCosts.serviceScope1Cost
						? parseInt(serviceScopeCosts.serviceScope1Cost, 10)
						: 0,
					lineItem2Cost: serviceScopeCosts.serviceScope2Cost
						? parseInt(serviceScopeCosts.serviceScope2Cost, 10)
						: 0,
					lineItem3Cost: serviceScopeCosts.serviceScope3Cost
						? parseInt(serviceScopeCosts.serviceScope3Cost, 10)
						: 0,
					lineItem4Cost: serviceScopeCosts.serviceScope4Cost
						? parseInt(serviceScopeCosts.serviceScope4Cost, 10)
						: 0,
					lineItem5Cost: serviceScopeCosts.serviceScope5Cost
						? parseInt(serviceScopeCosts.serviceScope5Cost, 10)
						: 0,
					lineItem6Cost: serviceScopeCosts.serviceScope6Cost
						? parseInt(serviceScopeCosts.serviceScope6Cost, 10)
						: 0,
					lineItem7Cost: serviceScopeCosts.serviceScope7Cost
						? parseInt(serviceScopeCosts.serviceScope7Cost, 10)
						: 0,
					lineItem8Cost: serviceScopeCosts.serviceScope8Cost
						? parseInt(serviceScopeCosts.serviceScope8Cost, 10)
						: 0,
					taskCreatedTime: dayjs().startOf("day").valueOf(),
					taskSubject: `RFA #${dealInformation.current_amendment_number} Submitted: ${dealInformation.name}. Amendment recipient is ${clientInformation.firstName} ${clientInformation.lastName} (${clientInformation.email}). RFA submitted by ${userName}.`,
					taskBody: `RFA #${dealInformation.current_amendment_number} Submitted: ${dealInformation.name}. Amendment recipient is ${clientInformation.firstName} ${clientInformation.lastName} (${clientInformation.email}). RFA submitted by ${userName}. Notes: ${amendmentInformation}.`,
					taskOwnerIDs: taskOwnerIDs,
				}
			);

			// if there is an error, display the error message
			if (res.data.error) {
				setIsSubmitting(false);
				errorMessageOpened(
					res.data.error ||
						"Error submitting RFA. Refresh your browser and try again. If the problem persists, contact your system administrator.",
					res.data.error
				);
				// log the error message
				await submitRFALog({
					errorMessage:
						res.data.error ||
						"Error submitting RFA. Refresh your browser and try again. If the problem persists, contact your system administrator.",
				});
			} else {
				// log the time the RFA submission was completed and amendment was created in HubSpot
				loggingPayload.current.amendmentCreatedTime =
					getCurrentTimeUTC();
				loggingPayload.current.submittedTime = getCurrentTimeUTC();
				setIsSubmitting(false);
				successMessageOpened("RFA submitted successfully");
				// log the successful RFA submission
				await submitRFALog({
					errorMessage: "",
					submittedTime: getCurrentTimeUTC(),
					amendmentCreatedTime: getCurrentTimeUTC(),
				});
				resetFormForNewRFA();
			}
		} catch (error) {
			setIsSubmitting(false);
			errorMessageOpened(
				"Error submitting RFA. Refresh your browser and try again. If the problem persists, contact your system administrator.",
				`Undefined error submitting RFA: ${error}`
			);
			// log the error message
			await submitRFALog({
				errorMessage:
					`Undefined error submitting RFA. Refresh your browser and try again. If the problem persists, contact your system administrator.` +
					` | Undefined error submitting RFA: ${error}`,
			});
		}
	};

	// Submit RFA Log
	const submitRFALog = async (payloadOverride = {}) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/rfa", {
				submittedByEmployeeID: employeeID,
				draftID: currentDraftID,
				hubspotDealID: dealInformation.id,
				recipientLookupMode: createNewClientEnabled
					? "new"
					: "existing",
				recipientEmail: clientInformation.email,
				recipientFirstName: clientInformation.firstName,
				recipientLastName: clientInformation.lastName,
				recipientHubspotID: clientInformation.recordID,

				scope1: serviceScopes.serviceScope1,
				scope2: serviceScopes.serviceScope2,
				scope3: serviceScopes.serviceScope3,
				scope4: serviceScopes.serviceScope4,
				scope5: serviceScopes.serviceScope5,
				scope6: serviceScopes.serviceScope6,
				scope7: serviceScopes.serviceScope7,
				scope8: serviceScopes.serviceScope8,

				scope1Cost: serviceScopeCosts.serviceScope1Cost
					? parseInt(serviceScopeCosts.serviceScope1Cost, 10)
					: 0,
				scope2Cost: serviceScopeCosts.serviceScope2Cost
					? parseInt(serviceScopeCosts.serviceScope2Cost, 10)
					: 0,
				scope3Cost: serviceScopeCosts.serviceScope3Cost
					? parseInt(serviceScopeCosts.serviceScope3Cost, 10)
					: 0,
				scope4Cost: serviceScopeCosts.serviceScope4Cost
					? parseInt(serviceScopeCosts.serviceScope4Cost, 10)
					: 0,
				scope5Cost: serviceScopeCosts.serviceScope5Cost
					? parseInt(serviceScopeCosts.serviceScope5Cost, 10)
					: 0,
				scope6Cost: serviceScopeCosts.serviceScope6Cost
					? parseInt(serviceScopeCosts.serviceScope6Cost, 10)
					: 0,
				scope7Cost: serviceScopeCosts.serviceScope7Cost
					? parseInt(serviceScopeCosts.serviceScope7Cost, 10)
					: 0,
				scope8Cost: serviceScopeCosts.serviceScope8Cost
					? parseInt(serviceScopeCosts.serviceScope8Cost, 10)
					: 0,

				amendmentInformation: amendmentInformation,
				propertyID: amendedScopeSelection.property?.id || null,
				projectID: amendedScopeSelection.project?.id || null,
				phaseID: amendedScopeSelection.phase?.id || null,
				modifiedBy: employeeID,

				// logging fields
				...loggingPayload.current,
				// overrides, freshest values last (wins on conflict)
				...payloadOverride,
			});
			// clear logging fields
			clearLoggingFields();
		} catch (error) {
			postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID: employeeID,
				date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
				message: "Error logging RFA submission",
				success: false,
				tool_name: "RFA",
			});
		}
	};

	// DRAFT FUNCTIONS
	const logDraftAutomation = async (success, message) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID,
				success,
				message,
				tool_name: "RFA Drafts",
			});
		} catch (error) {
			console.log("Error logging draft automation: ", error);
		}
	};

	const buildDraftFormFields = () => ({
		dealID: dealInformation.id,
		dealName: dealInformation.name,
		dealAmount: dealInformation.amount,
		dealProposalSignedDate: dealInformation.proposal_signed_date,
		dealCurrentAmendmentNumber: dealInformation.current_amendment_number,
		dealOIC: dealInformation.oic,

		recipientLookupMode: createNewClientEnabled ? "new" : "existing",
		recipientEmail: clientInformation.email,
		recipientFirstName: clientInformation.firstName,
		recipientLastName: clientInformation.lastName,
		recipientHubspotID: clientInformation.recordID,

		propertyID: amendedScopeSelection.property?.id || null,
		propertyName: amendedScopeSelection.property?.name || null,
		projectID: amendedScopeSelection.project?.id || null,
		projectName:
			amendedScopeSelection.project?.display_name ||
			amendedScopeSelection.project?.name ||
			null,
		phaseID: amendedScopeSelection.phase?.id || null,
		phaseName: amendedScopeSelection.phase?.name || null,

		contractType,

		scope1: serviceScopes.serviceScope1,
		scope2: serviceScopes.serviceScope2,
		scope3: serviceScopes.serviceScope3,
		scope4: serviceScopes.serviceScope4,
		scope5: serviceScopes.serviceScope5,
		scope6: serviceScopes.serviceScope6,
		scope7: serviceScopes.serviceScope7,
		scope8: serviceScopes.serviceScope8,

		scope1Cost: serviceScopeCosts.serviceScope1Cost,
		scope2Cost: serviceScopeCosts.serviceScope2Cost,
		scope3Cost: serviceScopeCosts.serviceScope3Cost,
		scope4Cost: serviceScopeCosts.serviceScope4Cost,
		scope5Cost: serviceScopeCosts.serviceScope5Cost,
		scope6Cost: serviceScopeCosts.serviceScope6Cost,
		scope7Cost: serviceScopeCosts.serviceScope7Cost,
		scope8Cost: serviceScopeCosts.serviceScope8Cost,

		additionalNotes,
	});

	// Compared against whichever baseline applies: the last loaded/saved
	// state of a draft, or (with no draft loaded) the form's just-reset/
	// freshly-mounted state - so a brand-new, never-saved RFA with real
	// content in it is also flagged as having unsaved changes.
	const currentFormSnapshot = JSON.stringify({
		draftName: currentDraftName,
		...buildDraftFormFields(),
	});
	const draftBaselineSnapshot = currentDraftID
		? lastSavedDraftSnapshotRef.current
		: pristineFormSnapshotRef.current;
	const isDraftDirty = Boolean(
		draftBaselineSnapshot !== null &&
			currentFormSnapshot !== draftBaselineSnapshot,
	);
	isDraftDirtyRef.current = isDraftDirty;

	const saveDraft = async (draftName) => {
		setIsSavingDraft(true);
		try {
			const payload = {
				employeeID,
				draftName,
				...buildDraftFormFields(),
			};
			if (currentDraftID) {
				await putAPICall(
					instance,
					accounts[0],
					`/api/rfa-drafts/${currentDraftID}`,
					payload,
				);
				logDraftAutomation(
					true,
					`Draft "${draftName}" updated (id ${currentDraftID})`,
				);
			} else {
				const res = await postAPICall(
					instance,
					accounts[0],
					"/api/rfa-drafts",
					payload,
				);
				setCurrentDraftID(res.data.insertId);
				logDraftAutomation(
					true,
					`Draft "${draftName}" created (id ${res.data.insertId})`,
				);
			}
			lastSavedDraftSnapshotRef.current = JSON.stringify({
				draftName,
				...buildDraftFormFields(),
			});
			successMessageOpened(`Draft "${draftName}" saved.`);
			return true;
		} catch (error) {
			logDraftAutomation(
				false,
				`Error saving draft "${draftName}": ${error}`,
			);
			errorMessageOpened(
				"Couldn't save this draft. Please try again.",
			);
			return false;
		} finally {
			setIsSavingDraft(false);
		}
	};

	const saveAsDraftClicked = () => {
		if (!currentDraftID) {
			setDraftNameInput("");
			setSaveDraftDialogOpen(true);
		} else {
			saveDraft(currentDraftName);
		}
	};

	const saveDraftDialogConfirmed = () => {
		const trimmedName = draftNameInput.trim();
		if (!trimmedName) return;
		setSaveDraftDialogOpen(false);
		setCurrentDraftName(trimmedName);
		saveDraft(trimmedName);
	};

	const resetFormForNewRFA = () => {
		clearInputFields();
		setCurrentDraftID(null);
		setCurrentDraftName("");
		lastSavedDraftSnapshotRef.current = null;
		setDraftLoadVersion((v) => v + 1);
	};

	const newRFAClicked = () => {
		if (isDraftDirty) {
			setNewRFAConfirmOpen(true);
		} else {
			resetFormForNewRFA();
		}
	};

	const newRFADiscardConfirmed = () => {
		setNewRFAConfirmOpen(false);
		resetFormForNewRFA();
	};

	const newRFASaveConfirmed = async () => {
		const saved = await saveDraft(currentDraftName);
		if (saved) {
			setNewRFAConfirmOpen(false);
			resetFormForNewRFA();
		}
	};

	const loadDraftIntoForm = (draft) => {
		setCurrentDraftID(draft.id);
		setCurrentDraftName(draft.draft_name);

		setDealInformation({
			id: draft.deal_id || "",
			name: draft.deal_name || "",
			amount: draft.deal_amount || "",
			proposal_signed_date: draft.deal_proposal_signed_date || "",
			current_amendment_number: draft.deal_current_amendment_number || 0,
			oic: draft.deal_oic || "",
		});
		// Mirrors the createNewClientClicked/searchExistingClientClicked
		// fix below - force the deal-dependent section open, since nothing
		// else here re-derives that visibility state from dealInformation.
		setDealDetailedViewOpen(true);

		if (draft.recipient_lookup_mode === "new") {
			createNewClientClicked();
		} else {
			searchExistingClientClicked(true);
		}
		setClientInformation({
			email: draft.recipient_email || "",
			firstName: draft.recipient_first_name || "",
			lastName: draft.recipient_last_name || "",
			recordID: draft.recipient_hubspot_id || "",
		});
		// createNewClientClicked/searchExistingClientClicked above both set
		// clientDetailedViewOpen themselves (true/false respectively, and
		// createNewClientClicked's early-return guard can skip it entirely),
		// so force it open here, after those calls, to guarantee the fields
		// are actually visible for a loaded draft.
		setClientDetailedViewOpen(true);

		setAmendedScopeSelection({
			property: draft.property_id
				? { id: draft.property_id, name: draft.property_name || "" }
				: null,
			project: draft.project_id
				? {
						id: draft.project_id,
						name: draft.project_name || "",
						display_name: draft.project_name || "",
				  }
				: null,
			// project_id is included so the deal->project/property
			// auto-derivation effect (keyed on dealInformation.id) doesn't
			// wipe this back out - it only keeps a restored phase when its
			// project_id matches the deal's derived project.
			phase: draft.phase_id
				? {
						id: draft.phase_id,
						name: draft.phase_name || "",
						project_id: draft.project_id || null,
				  }
				: null,
		});

		setContractType(draft.contract_type || "");

		setServiceScopes({
			serviceScope1: draft.scope_one || "",
			serviceScope2: draft.scope_two || "",
			serviceScope3: draft.scope_three || "",
			serviceScope4: draft.scope_four || "",
			serviceScope5: draft.scope_five || "",
			serviceScope6: draft.scope_six || "",
			serviceScope7: draft.scope_seven || "",
			serviceScope8: draft.scope_eight || "",
		});
		setServiceScopeCosts({
			serviceScope1Cost: draft.scope_one_cost || "",
			serviceScope2Cost: draft.scope_two_cost || "",
			serviceScope3Cost: draft.scope_three_cost || "",
			serviceScope4Cost: draft.scope_four_cost || "",
			serviceScope5Cost: draft.scope_five_cost || "",
			serviceScope6Cost: draft.scope_six_cost || "",
			serviceScope7Cost: draft.scope_seven_cost || "",
			serviceScope8Cost: draft.scope_eight_cost || "",
		});
		const activeScopeCount = [
			draft.scope_one,
			draft.scope_two,
			draft.scope_three,
			draft.scope_four,
			draft.scope_five,
			draft.scope_six,
			draft.scope_seven,
			draft.scope_eight,
		].filter((scope) => scope && scope.trim()).length;
		setNumberOfServices(Math.max(1, activeScopeCount));

		setAdditionalNotes(draft.additional_notes || "");

		successMessageOpened(`Draft "${draft.draft_name}" loaded.`);
		setDraftLoadVersion((v) => v + 1);
	};

	return (
		<div className="RFA">
			{!isMobile && (
			<div className="rfaHeaderContainer">
				<div className="rfaHeader">RFA</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "1em",
					}}
				>
					{currentDraftID && (
						<>
							<Chip
								label={`Editing draft: ${currentDraftName}`}
								sx={{
									backgroundColor: "rgba(24, 76, 147, 0.12)",
									color: "#184C93",
								}}
							/>
							<Button
								variant="text"
								size="small"
								onClick={newRFAClicked}
							>
								New RFA
							</Button>
						</>
					)}
					<Button
						variant="outlined"
						size="small"
						onClick={() => setDraftsDrawerOpen(true)}
					>
						My Drafts
					</Button>
				</div>
			</div>
			)}
			<div className="rfaCardDialogRow" id="successDialogDiv">
				<Collapse in={isSuccess} id="dialogCollapse">
					<Alert
						sx={{ marginTop: "1em", marginBottom: "1em" }}
						severity="success"
						onClose={successMessageClosed}
					>
						{successMessage}
					</Alert>
				</Collapse>
			</div>
			{validationIssues.length > 0 && (
				<div className="rfaCardDialogRow">
					<Alert
						severity="error"
						onClose={validationSummaryClosed}
						sx={{ mt: 1, mb: 1, boxSizing: "border-box" }}
					>
						<AlertTitle>Please fix these before submitting</AlertTitle>
						<List dense disablePadding>
							{validationIssues.map((issue) => (
								<ListItem key={issue.fieldName} disablePadding>
									<ListItemButton
										dense
										onClick={() =>
											focusValidationField(issue.fieldName)
										}
									>
										<ListItemText primary={issue.message} />
									</ListItemButton>
								</ListItem>
							))}
						</List>
					</Alert>
				</div>
			)}
			<div className="rfaContainer">
			<div className="rfaCardColumn">
			<div className="rfaCard">
				<div className="cardHeader">
					<h2>Deal Information</h2>
				</div>
				<div className="rfaCardRow">
					<Autocomplete
						sx={{ flexGrow: 1 }}
						options={dealOptions}
						size={ELEMENT_SIZE}
						ref={dealNameRef}
						getOptionLabel={(option) =>
							option.properties
								? option.properties.dealname
								: option
						}
						filterSelectedOptions
						onChange={dealNameSelected}
						clearOnBlur={false}
						renderInput={(params) => (
							<TextField
								{...params}
								id="rfaDealName"
								label="Deal Name"
								placeholder="Deal Name"
								value={dealInformation.name || ""}
								onKeyUp={dealNameChanged}
								error={Boolean(fieldErrors.dealName)}
								helperText={
									fieldErrors.dealName ||
									(dealInformation.id
										? undefined
										: "Showing deals that are 'Active' or 'On Hold'")
								}
							/>
						)}
					/>
				</div>
				<div className="rfaCardRow">
					<TextField
						sx={{ flexGrow: 1 }}
						size={ELEMENT_SIZE}
						disabled
						label="Amount (USD)"
						value={dealInformation.amount || ""}
					/>
					<TextField
						sx={{ flexGrow: 1 }}
						size={ELEMENT_SIZE}
						disabled
						label="Proposal Signed Date"
						value={dealInformation.proposal_signed_date || ""}
					/>
				</div>
			</div>
			<div
				className={`rfaCard${
					dealDetailedViewOpen ? "" : " rfaCardDisabled"
				}`}
				id="scopeOfServicesDiv"
			>
					<div className="cardHeader">
						<div className="rfaScopeTitle">
							<h2>Scope of Service(s)</h2>
							<Tooltip
								title={
									<>
										The information you put here will be
										included on the client facing document{" "}
										<em>exactly</em> as written.
									</>
								}
							>
								<span>
									<IconButton aria-label="info" disabled>
										<InfoIcon />
									</IconButton>
								</span>
							</Tooltip>
						</div>

						<ButtonGroup>
							<Button
								variant="outlined"
								color="error"
								size={ELEMENT_SIZE}
								onClick={removeServicePressed}
							>
								Remove
							</Button>
							<Button
								variant="contained"
								color="primary"
								size={ELEMENT_SIZE}
								onClick={addServicePressed}
							>
								Add
							</Button>
						</ButtonGroup>
					</div>
					<div className="rfaCardRow">
						<FormControl
							sx={{ flexGrow: 1 }}
							size={ELEMENT_SIZE}
							error={Boolean(fieldErrors.contractType)}
						>
							<InputLabel id="rfaContractTypeLabel">
								Contract Type
							</InputLabel>
							<Select
								labelId="rfaContractTypeLabel"
								id="rfaContractType"
								value={contractType}
								label="Contract Type"
								onChange={contractTypeChanged}
								size={ELEMENT_SIZE}
							>
								<MenuItem value="fixed_fee">Fixed Fee</MenuItem>
								<MenuItem value="time_and_cost">
									Time &amp; Cost
								</MenuItem>
							</Select>
						</FormControl>
					</div>
					{fieldErrors.contractType && (
						<div className="rfaFieldError">
							{fieldErrors.contractType}
						</div>
					)}
					<div className="rfaServiceTextFields">
						<div className="rfaServiceTextFieldsRow">
							<TextField
								id="rfaServiceScope1"
								sx={{ flex: 5 }}
								size={ELEMENT_SIZE}
								name="serviceScope1"
								label="Service 1"
								value={serviceScopes.serviceScope1 || ""}
								onChange={serviceScopeChanged}
								error={Boolean(fieldErrors.serviceScope1)}
								helperText={fieldErrors.serviceScope1 || ""}
							/>
							<TextField
								id="rfaServiceScope1Cost"
								sx={{ flex: 1 }}
								size={ELEMENT_SIZE}
								label="Estimated Cost"
								value={
									serviceScopeCosts.serviceScope1Cost || ""
								}
								name="serviceScope1Cost"
								onChange={serviceScopeCostChanged}
								error={Boolean(fieldErrors.serviceScope1Cost)}
								helperText={fieldErrors.serviceScope1Cost || ""}
							/>
						</div>
						{numberOfServices > 1 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope2"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 2"
									value={serviceScopes.serviceScope2 || ""}
									name="serviceScope2"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope2)}
									helperText={fieldErrors.serviceScope2 || ""}
								/>
								<TextField
									id="rfaServiceScope2Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope2Cost ||
										""
									}
									name="serviceScope2Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope2Cost)}
									helperText={fieldErrors.serviceScope2Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 2 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope3"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 3"
									value={serviceScopes.serviceScope3 || ""}
									name="serviceScope3"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope3)}
									helperText={fieldErrors.serviceScope3 || ""}
								/>
								<TextField
									id="rfaServiceScope3Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope3Cost ||
										""
									}
									name="serviceScope3Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope3Cost)}
									helperText={fieldErrors.serviceScope3Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 3 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope4"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 4"
									value={serviceScopes.serviceScope4 || ""}
									name="serviceScope4"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope4)}
									helperText={fieldErrors.serviceScope4 || ""}
								/>
								<TextField
									id="rfaServiceScope4Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope4Cost ||
										""
									}
									name="serviceScope4Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope4Cost)}
									helperText={fieldErrors.serviceScope4Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 4 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope5"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 5"
									value={serviceScopes.serviceScope5 || ""}
									name="serviceScope5"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope5)}
									helperText={fieldErrors.serviceScope5 || ""}
								/>
								<TextField
									id="rfaServiceScope5Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope5Cost ||
										""
									}
									name="serviceScope5Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope5Cost)}
									helperText={fieldErrors.serviceScope5Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 5 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope6"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 6"
									value={serviceScopes.serviceScope6 || ""}
									name="serviceScope6"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope6)}
									helperText={fieldErrors.serviceScope6 || ""}
								/>
								<TextField
									id="rfaServiceScope6Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope6Cost ||
										""
									}
									name="serviceScope6Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope6Cost)}
									helperText={fieldErrors.serviceScope6Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 6 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope7"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 7"
									value={serviceScopes.serviceScope7 || ""}
									name="serviceScope7"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope7)}
									helperText={fieldErrors.serviceScope7 || ""}
								/>
								<TextField
									id="rfaServiceScope7Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope7Cost ||
										""
									}
									name="serviceScope7Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope7Cost)}
									helperText={fieldErrors.serviceScope7Cost || ""}
								/>
							</div>
						)}
						{numberOfServices > 7 && (
							<div className="rfaServiceTextFieldsRow">
								<TextField
									id="rfaServiceScope8"
									sx={{ flex: 5 }}
									size={ELEMENT_SIZE}
									label="Service 8"
									value={serviceScopes.serviceScope8 || ""}
									name="serviceScope8"
									onChange={serviceScopeChanged}
									error={Boolean(fieldErrors.serviceScope8)}
									helperText={fieldErrors.serviceScope8 || ""}
								/>
								<TextField
									id="rfaServiceScope8Cost"
									sx={{ flex: 1 }}
									size={ELEMENT_SIZE}
									label="Estimated Cost"
									value={
										serviceScopeCosts.serviceScope8Cost ||
										""
									}
									name="serviceScope8Cost"
									onChange={serviceScopeCostChanged}
									error={Boolean(fieldErrors.serviceScope8Cost)}
									helperText={fieldErrors.serviceScope8Cost || ""}
								/>
							</div>
						)}
					</div>
					<div className="rfaScopeMobileNote">
						The information you put here will be included on the
						client facing document <em>exactly</em> as written.
					</div>
				</div>
			</div>
			<div className="rfaCardColumn">
			<div
				className={`rfaCard${
					dealDetailedViewOpen ? "" : " rfaCardDisabled"
				}`}
				id="clientInformationDiv"
			>
					<div className="cardHeader">
						<h2>Recipient Information</h2>
						<ButtonGroup>
							<Button
								variant={
									createNewClientEnabled
										? "contained"
										: "outlined"
								}
								onClick={createNewClientClicked}
								size={ELEMENT_SIZE}
							>
								Create New
							</Button>
							<Button
								variant={
									searchExistingClientEnabled
										? "contained"
										: "outlined"
								}
								onClick={searchExistingClientClicked}
								size={ELEMENT_SIZE}
							>
								Search Existing
							</Button>
						</ButtonGroup>
					</div>
					<Collapse in={clientSearchBarOpen} id="searchBarCollapse">
						<div
							className="searchByRFACardRow"
							id="clientSearchByFields"
						>
							<Autocomplete
								sx={{ flexGrow: 1 }}
								options={clientOptions}
								size={ELEMENT_SIZE}
								ref={clientSearchByValueRef}
								getOptionLabel={(option) =>
									option.properties
										? `${
												option.properties.firstname ||
												""
										  } ${
												option.properties.lastname || ""
										  } (${option.properties.email || ""})`
										: option
								}
								filterSelectedOptions
								onChange={clientSearchByValueSelected}
								clearOnBlur={false}
								renderInput={(params) => (
									<TextField
										{...params}
										label={searchByOptionsMap.get(
											clientSearchByParameter
										)}
										placeholder={searchByOptionsMap.get(
											clientSearchByParameter
										)}
										value={clientSearchByValue || ""}
										onKeyUp={clientSearchByValueChanged}
									/>
								)}
							/>
							<FormControl sx={{ width: "20%" }}>
								<InputLabel id="clientSearchByLabel">
									Search By
								</InputLabel>
								<Select
									labelId="clientSearchByLabel"
									value={clientSearchByParameter}
									label="Search By"
									onChange={clientSearchByParameterSelected}
									size={ELEMENT_SIZE}
								>
									<MenuItem value="lastname">
										Last Name
									</MenuItem>
									<MenuItem value="email">Email</MenuItem>
								</Select>
							</FormControl>
						</div>
						{fieldErrors.recipientSelection && (
							<div className="rfaFieldError">
								{fieldErrors.recipientSelection}
							</div>
						)}
					</Collapse>
					<Collapse
						in={clientDetailedViewOpen}
						id="detailedViewCollapse"
					>
						<div className="rfaCardRow">
							<TextField
								id="rfaRecipientEmail"
								sx={{ flexGrow: 1 }}
								size={ELEMENT_SIZE}
								name="email"
								disabled={!createNewClientEnabled}
								onChange={clientInformationChanged}
								label="Email"
								value={clientInformation.email || ""}
								error={Boolean(fieldErrors.recipientEmail)}
								helperText={fieldErrors.recipientEmail || ""}
							/>
						</div>
						<div className="rfaCardRow">
							<TextField
								id="rfaRecipientFirstName"
								sx={{ flexGrow: 1 }}
								disabled={!createNewClientEnabled}
								size={ELEMENT_SIZE}
								name="firstName"
								onChange={clientInformationChanged}
								label="First Name"
								value={clientInformation.firstName || ""}
								error={Boolean(fieldErrors.recipientFirstName)}
								helperText={fieldErrors.recipientFirstName || ""}
							/>
							<TextField
								id="rfaRecipientLastName"
								sx={{ flexGrow: 1 }}
								disabled={!createNewClientEnabled}
								onChange={clientInformationChanged}
								size={ELEMENT_SIZE}
								name="lastName"
								label="Last Name"
								value={clientInformation.lastName || ""}
								error={Boolean(fieldErrors.recipientLastName)}
								helperText={fieldErrors.recipientLastName || ""}
							/>
						</div>
					</Collapse>
				</div>
			<div
				className={`rfaCard${
					dealDetailedViewOpen ? "" : " rfaCardDisabled"
				}`}
				id="amendedScopeDiv"
			>
					<div className="cardHeader">
						<h2>Property / Project / Phase Information</h2>
					</div>
					<div className="rfaCardRow">
						<Autocomplete
							sx={{ flexGrow: 1 }}
							options={propertyOptions}
							size={ELEMENT_SIZE}
							value={amendedScopeSelection.property}
							getOptionLabel={(option) => option?.name || ""}
							isOptionEqualToValue={(option, value) =>
								option.id === value.id
							}
							onChange={amendedPropertySelected}
							renderInput={(params) => (
								<TextField
									{...params}
									id="rfaAmendedProperty"
									label="Property"
									error={Boolean(fieldErrors.amendedProperty)}
									helperText={
										fieldErrors.amendedProperty || ""
									}
								/>
							)}
						/>
						<Autocomplete
							sx={{ flexGrow: 1 }}
							options={filteredProjectOptions}
							size={ELEMENT_SIZE}
							value={amendedScopeSelection.project}
							disabled={!amendedScopeSelection.property}
							getOptionLabel={(option) =>
								option?.display_name || option?.name || ""
							}
							isOptionEqualToValue={(option, value) =>
								option.id === value.id
							}
							onChange={amendedProjectSelected}
							renderInput={(params) => (
								<TextField
									{...params}
									id="rfaAmendedProject"
									label="Project"
									error={Boolean(fieldErrors.amendedProject)}
									helperText={
										fieldErrors.amendedProject || ""
									}
								/>
							)}
						/>
						<Autocomplete
							sx={{ flexGrow: 1 }}
							options={filteredPhaseOptions}
							size={ELEMENT_SIZE}
							value={amendedScopeSelection.phase}
							disabled={!amendedScopeSelection.project}
							getOptionLabel={(option) => option?.name || ""}
							isOptionEqualToValue={(option, value) =>
								option.id === value.id
							}
							onChange={amendedPhaseSelected}
							renderInput={(params) => (
								<TextField
									{...params}
									id="rfaAmendedPhase"
									label="Phase"
									error={Boolean(fieldErrors.amendedPhase)}
									helperText={
										fieldErrors.amendedPhase || ""
									}
								/>
							)}
						/>
					</div>
				</div>
			<div
				className={`rfaCard${
					dealDetailedViewOpen ? "" : " rfaCardDisabled"
				}`}
			>
					<div className="cardHeader">
						<h2>Amendment Information</h2>
					</div>
					<div className="rfaCardSectionNote">
						<Tooltip title="Project and phase information is added to the beginning of the amendment information, before your additional notes.">
							<span>
								<IconButton
									aria-label="project information help"
									size="small"
									disabled
								>
									<InfoIcon fontSize="inherit" />
								</IconButton>
							</span>
						</Tooltip>
						Project being amended: {pppName || "Not selected"}
					</div>
					<div className="rfaCardRow">
						<J2MultilineTextField
							label="Additional Notes"
							value={additionalNotes || ""}
							placeholder="Additional Notes"
							onChange={additionalNotesChanged}
						/>
					</div>
				</div>
			</div>
			</div>
			<div className="rfaButtonContainer">
				{!isMobile && (
				<Button
					color="primary"
					variant="outlined"
					size={ELEMENT_SIZE}
					onClick={saveAsDraftClicked}
					disabled={isSavingDraft || isSubmitting}
				>
					{currentDraftID ? "Update Draft" : "Save as Draft"}
				</Button>
				)}
				<div
					className={`rfaSubmitButtonContainer${
						dealDetailedViewOpen ? "" : " rfaCardDisabled"
					}`}
				>
					<Button
						color="primary"
						variant="contained"
						size={ELEMENT_SIZE}
						onClick={submitRFAPressed}
						disabled={isSubmitting || !dealDetailedViewOpen}
					>
						Submit
					</Button>
					{isSubmitting && (
						<CircularProgress
							size={24}
							sx={{
								color: "primary",
								position: "absolute",
								top: "50%",
								left: "50%",
								marginTop: "-12px",
								marginLeft: "-12px",
							}}
						/>
					)}
				</div>
			</div>
			<J2ErrorDialog
				open={isError}
				onClose={errorMessageClosed}
				errorMessage={errorMessage}
			/>
			<Dialog
				open={saveDraftDialogOpen}
				onClose={() => setSaveDraftDialogOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle id="rfaSaveDraftDialogTitle">
					Name this draft
				</DialogTitle>
				<DialogContent id="rfaSaveDraftDialogContent">
					<TextField
						autoFocus
						fullWidth
						label="Draft Name"
						value={draftNameInput}
						onChange={(event) => setDraftNameInput(event.target.value)}
						sx={{ marginTop: "0.5em" }}
					/>
					<div className="rfaDialogButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={() => setSaveDraftDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							disabled={!draftNameInput.trim()}
							onClick={saveDraftDialogConfirmed}
						>
							Save
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<Dialog
				open={newRFAConfirmOpen}
				onClose={() => setNewRFAConfirmOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle id="rfaNewRFAConfirmDialogTitle">
					Save changes before starting a new RFA?
				</DialogTitle>
				<DialogContent id="rfaNewRFAConfirmDialogContent">
					<Typography variant="body2">
						You're currently editing the draft "{currentDraftName}".
						Save your changes to it, or discard them and start a new
						RFA?
					</Typography>
					<div className="rfaDialogButtons">
						<Button
							variant="text"
							onClick={() => setNewRFAConfirmOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="outlined"
							color="error"
							onClick={newRFADiscardConfirmed}
						>
							Discard
						</Button>
						<Button
							variant="contained"
							onClick={newRFASaveConfirmed}
							disabled={isSavingDraft}
						>
							Save
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<RFADraftsDrawer
				open={!isMobile && draftsDrawerOpen}
				onClose={() => setDraftsDrawerOpen(false)}
				instance={instance}
				account={accounts[0]}
				employeeID={employeeID}
				onOpenDraft={loadDraftIntoForm}
				logAutomation={logDraftAutomation}
			/>
		</div>
	);
};

export default RFA;
